import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, Shield, Key, RefreshCw, Trash2, Mail,
  UserPlus, Check, X, Eye, EyeOff, AlertCircle,
  ChevronDown, Lock, RotateCcw, Settings
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminManagers = () => {
  const navigate = useNavigate();
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canCreate, setCanCreate] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Modal states
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(null);
  const [showReset2FAModal, setShowReset2FAModal] = useState(null);
  const [showResetPinModal, setShowResetPinModal] = useState(null);
  const [showReregisterModal, setShowReregisterModal] = useState(null);
  
  // Form values
  const [newPassword, setNewPassword] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [newPin, setNewPin] = useState('');
  
  const adminToken = localStorage.getItem('adminToken');

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin');
      return;
    }
    loadManagers();
    checkCanCreate();
  }, [adminToken, navigate]);

  const loadManagers = async () => {
    try {
      const response = await axios.get(`${API}/manager/list`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setManagers(response.data.managers);
    } catch (error) {
      console.error('Load managers error:', error);
      if (error.response?.status === 401) {
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const checkCanCreate = async () => {
    try {
      const response = await axios.get(`${API}/manager/check-exists`);
      setCanCreate(response.data.can_create);
    } catch (error) {
      console.error('Check error:', error);
    }
  };

  const resetPassword = async (managerId) => {
    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API}/manager/${managerId}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      setNewPassword(response.data.new_password);
      toast.success('Wachtwoord gereset. E-mail verzonden naar beheerder.');
    } catch (error) {
      toast.error('Reset mislukt');
    } finally {
      setActionLoading(false);
    }
  };

  const reset2FA = async (managerId) => {
    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API}/manager/${managerId}/reset-2fa`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      setQrCode(response.data.qr_code);
      toast.success('2FA gereset. Deel de QR code met de beheerder.');
    } catch (error) {
      toast.error('Reset mislukt');
    } finally {
      setActionLoading(false);
    }
  };

  const resetPin = async (managerId) => {
    if (newPin.length !== 4) {
      toast.error('PIN moet 4 cijfers zijn');
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/manager/${managerId}/reset-pin`,
        { new_pin: newPin },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success('PIN succesvol gewijzigd');
      setShowResetPinModal(null);
      setNewPin('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset mislukt');
    } finally {
      setActionLoading(false);
    }
  };

  const reregisterManager = async (managerId) => {
    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API}/manager/${managerId}/reregister`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      setQrCode(response.data.qr_code);
      setNewPassword(response.data.temp_password || '');
      toast.success('Account gereset. Beheerder moet opnieuw registreren.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Her-registratie mislukt');
    } finally {
      setActionLoading(false);
    }
  };

  const deleteManager = async (managerId) => {
    if (!confirm('Weet je zeker dat je deze beheerder wilt verwijderen?')) return;
    
    try {
      await axios.delete(`${API}/manager/${managerId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Beheerder verwijderd');
      loadManagers();
      checkCanCreate();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  // Fixed positions for Manager 1 and Manager 2
  const manager1 = managers[0] || null;
  const manager2 = managers[1] || null;

  return (
    <div className="min-h-screen bg-[#050505] p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Beheerders</h1>
            <p className="text-[#57534E]">Beheer de manager accounts (max. 2)</p>
          </div>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="px-4 py-2 text-[#A8A29E] hover:text-white transition-colors"
          >
            ← Terug
          </button>
        </div>

        {/* Info Banner */}
        <div className="glass rounded-xl p-4 mb-6 border border-blue-500/30">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-blue-400 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="text-white font-medium">Manager Portaal</p>
              <p className="text-[#A8A29E] text-sm mt-1">
                Beheerders kunnen inloggen via <span className="text-[#D4AF37]">/manager/login</span>. 
                Gebruik de dropdown menu's hieronder om accounts te beheren.
              </p>
            </div>
          </div>
        </div>

        {/* Manager Cards Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Manager 1 Card */}
          <div className="glass rounded-xl overflow-hidden">
            <div className="p-4 border-b border-[#2C1B18] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <span className="text-[#D4AF37] font-bold">1</span>
                </div>
                <span className="text-white font-medium">Manager 1</span>
              </div>
              
              {manager1 ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 px-3 py-2 bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 border border-[#D4AF37]/30 rounded-lg text-[#D4AF37] transition-colors">
                      <Settings size={16} />
                      <span className="text-sm">Acties</span>
                      <ChevronDown size={14} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[#1C1917] border-[#2C1B18] w-56">
                    <DropdownMenuItem 
                      onClick={() => { setShowResetPasswordModal(manager1.id); setNewPassword(''); }}
                      className="text-white hover:bg-white/5 cursor-pointer"
                    >
                      <Key size={16} className="mr-2 text-[#D4AF37]" />
                      Wachtwoord Reset
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => { setShowReset2FAModal(manager1.id); setQrCode(''); }}
                      className="text-white hover:bg-white/5 cursor-pointer"
                    >
                      <RefreshCw size={16} className="mr-2 text-blue-400" />
                      2FA Authenticator Reset
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => { setShowResetPinModal(manager1.id); setNewPin(''); }}
                      className="text-white hover:bg-white/5 cursor-pointer"
                    >
                      <Lock size={16} className="mr-2 text-purple-400" />
                      Pincode Reset
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-[#2C1B18]" />
                    <DropdownMenuItem 
                      onClick={() => { setShowReregisterModal(manager1.id); setQrCode(''); setNewPassword(''); }}
                      className="text-white hover:bg-white/5 cursor-pointer"
                    >
                      <RotateCcw size={16} className="mr-2 text-orange-400" />
                      Account Reset (Her-registratie)
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-[#2C1B18]" />
                    <DropdownMenuItem 
                      onClick={() => deleteManager(manager1.id)}
                      className="text-red-400 hover:bg-red-500/10 cursor-pointer"
                    >
                      <Trash2 size={16} className="mr-2" />
                      Verwijderen
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <span className="text-[#57534E] text-sm">Niet toegewezen</span>
              )}
            </div>
            
            <div className="p-4">
              {manager1 ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                      <Shield size={24} className="text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{manager1.username}</p>
                      <p className="text-[#A8A29E] text-sm">{manager1.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded ${
                      manager1.is_2fa_enabled 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {manager1.is_2fa_enabled ? '2FA Actief' : '2FA Inactief'}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      manager1.is_active 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {manager1.is_active ? 'Actief' : 'Inactief'}
                    </span>
                  </div>
                  {manager1.last_login && (
                    <p className="text-[#57534E] text-xs">
                      Laatst ingelogd: {new Date(manager1.last_login).toLocaleString('nl-NL')}
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Shield size={40} className="mx-auto text-[#57534E] mb-3" />
                  <p className="text-[#A8A29E] text-sm mb-4">Geen manager toegewezen</p>
                  <button
                    onClick={() => window.open('/manager/login', '_blank')}
                    className="text-[#D4AF37] hover:underline text-sm"
                  >
                    Registreer via /manager/login
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Manager 2 Card */}
          <div className="glass rounded-xl overflow-hidden">
            <div className="p-4 border-b border-[#2C1B18] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <span className="text-purple-400 font-bold">2</span>
                </div>
                <span className="text-white font-medium">Manager 2</span>
              </div>
              
              {manager2 ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 px-3 py-2 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 rounded-lg text-purple-400 transition-colors">
                      <Settings size={16} />
                      <span className="text-sm">Acties</span>
                      <ChevronDown size={14} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[#1C1917] border-[#2C1B18] w-56">
                    <DropdownMenuItem 
                      onClick={() => { setShowResetPasswordModal(manager2.id); setNewPassword(''); }}
                      className="text-white hover:bg-white/5 cursor-pointer"
                    >
                      <Key size={16} className="mr-2 text-[#D4AF37]" />
                      Wachtwoord Reset
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => { setShowReset2FAModal(manager2.id); setQrCode(''); }}
                      className="text-white hover:bg-white/5 cursor-pointer"
                    >
                      <RefreshCw size={16} className="mr-2 text-blue-400" />
                      2FA Authenticator Reset
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => { setShowResetPinModal(manager2.id); setNewPin(''); }}
                      className="text-white hover:bg-white/5 cursor-pointer"
                    >
                      <Lock size={16} className="mr-2 text-purple-400" />
                      Pincode Reset
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-[#2C1B18]" />
                    <DropdownMenuItem 
                      onClick={() => { setShowReregisterModal(manager2.id); setQrCode(''); setNewPassword(''); }}
                      className="text-white hover:bg-white/5 cursor-pointer"
                    >
                      <RotateCcw size={16} className="mr-2 text-orange-400" />
                      Account Reset (Her-registratie)
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-[#2C1B18]" />
                    <DropdownMenuItem 
                      onClick={() => deleteManager(manager2.id)}
                      className="text-red-400 hover:bg-red-500/10 cursor-pointer"
                    >
                      <Trash2 size={16} className="mr-2" />
                      Verwijderen
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <span className="text-[#57534E] text-sm">Niet toegewezen</span>
              )}
            </div>
            
            <div className="p-4">
              {manager2 ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center">
                      <Shield size={24} className="text-purple-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{manager2.username}</p>
                      <p className="text-[#A8A29E] text-sm">{manager2.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded ${
                      manager2.is_2fa_enabled 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {manager2.is_2fa_enabled ? '2FA Actief' : '2FA Inactief'}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      manager2.is_active 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {manager2.is_active ? 'Actief' : 'Inactief'}
                    </span>
                  </div>
                  {manager2.last_login && (
                    <p className="text-[#57534E] text-xs">
                      Laatst ingelogd: {new Date(manager2.last_login).toLocaleString('nl-NL')}
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Shield size={40} className="mx-auto text-[#57534E] mb-3" />
                  <p className="text-[#A8A29E] text-sm mb-4">Geen manager toegewezen</p>
                  {canCreate && (
                    <button
                      onClick={() => window.open('/manager/login', '_blank')}
                      className="text-purple-400 hover:underline text-sm"
                    >
                      Registreer via /manager/login
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Info */}
        <div className="glass rounded-xl p-4">
          <p className="text-[#A8A29E] text-sm text-center">
            Manager Portaal: <span className="text-[#D4AF37]">/manager/login</span> • 
            {' '}Beheerders beschikbaar: <span className="text-white font-medium">{2 - managers.length}</span>
          </p>
        </div>
      </div>

      {/* Reset Password Modal */}
      {showResetPasswordModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="glass rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Key className="text-[#D4AF37]" size={20} />
                Wachtwoord Reset
              </h2>
              <button onClick={() => setShowResetPasswordModal(null)} className="p-2 hover:bg-white/5 rounded-lg text-[#A8A29E]">
                <X size={20} />
              </button>
            </div>
            
            {!newPassword ? (
              <>
                <p className="text-[#A8A29E] mb-4">
                  Dit genereert een nieuw wachtwoord en stuurt het naar de beheerder per e-mail.
                </p>
                <div className="flex gap-2">
                  <button onClick={() => setShowResetPasswordModal(null)} className="flex-1 py-2 border border-[#2C1B18] text-[#A8A29E] rounded-lg hover:bg-white/5">
                    Annuleren
                  </button>
                  <button onClick={() => resetPassword(showResetPasswordModal)} disabled={actionLoading} className="flex-1 py-2 bg-[#D4AF37] text-black font-medium rounded-lg hover:bg-[#E5C048] disabled:opacity-50">
                    {actionLoading ? 'Resetten...' : 'Reset Wachtwoord'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-[#A8A29E] mb-4">Nieuw wachtwoord gegenereerd:</p>
                <div className="p-4 bg-[#1C1917] rounded-lg mb-4">
                  <p className="text-[#D4AF37] font-mono text-lg text-center select-all">{newPassword}</p>
                </div>
                <p className="text-[#57534E] text-sm mb-4">Dit wachtwoord is ook per e-mail verzonden.</p>
                <button onClick={() => setShowResetPasswordModal(null)} className="w-full py-2 bg-[#D4AF37] text-black font-medium rounded-lg">
                  Sluiten
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Reset 2FA Modal */}
      {showReset2FAModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="glass rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <RefreshCw className="text-blue-400" size={20} />
                2FA Reset
              </h2>
              <button onClick={() => setShowReset2FAModal(null)} className="p-2 hover:bg-white/5 rounded-lg text-[#A8A29E]">
                <X size={20} />
              </button>
            </div>
            
            {!qrCode ? (
              <>
                <p className="text-[#A8A29E] mb-4">
                  Dit genereert een nieuwe 2FA QR code. De beheerder moet deze opnieuw scannen met Microsoft Authenticator.
                </p>
                <div className="flex gap-2">
                  <button onClick={() => setShowReset2FAModal(null)} className="flex-1 py-2 border border-[#2C1B18] text-[#A8A29E] rounded-lg hover:bg-white/5">
                    Annuleren
                  </button>
                  <button onClick={() => reset2FA(showReset2FAModal)} disabled={actionLoading} className="flex-1 py-2 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50">
                    {actionLoading ? 'Resetten...' : 'Reset 2FA'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-[#A8A29E] mb-4 text-center">Deel deze QR code met de beheerder:</p>
                <div className="flex justify-center mb-4">
                  <div className="p-4 bg-white rounded-xl">
                    <img src={qrCode} alt="QR Code" className="w-48 h-48" />
                  </div>
                </div>
                <p className="text-[#57534E] text-sm mb-4 text-center">Scan met Microsoft Authenticator app.</p>
                <button onClick={() => setShowReset2FAModal(null)} className="w-full py-2 bg-[#D4AF37] text-black font-medium rounded-lg">
                  Sluiten
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Reset PIN Modal */}
      {showResetPinModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="glass rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Lock className="text-purple-400" size={20} />
                Pincode Reset
              </h2>
              <button onClick={() => setShowResetPinModal(null)} className="p-2 hover:bg-white/5 rounded-lg text-[#A8A29E]">
                <X size={20} />
              </button>
            </div>
            
            <p className="text-[#A8A29E] mb-4">Voer een nieuwe 4-cijferige PIN in voor deze beheerder:</p>
            
            <div className="mb-4">
              <input
                type="password"
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                className="w-full bg-[#1C1917] border border-[#2C1B18] rounded-lg px-4 py-3 text-white text-center text-2xl tracking-[0.5em] focus:border-[#D4AF37] outline-none"
                placeholder="• • • •"
                maxLength={4}
              />
              <p className="text-[#57534E] text-xs mt-2 text-center">Voer exact 4 cijfers in</p>
            </div>
            
            <div className="flex gap-2">
              <button onClick={() => setShowResetPinModal(null)} className="flex-1 py-2 border border-[#2C1B18] text-[#A8A29E] rounded-lg hover:bg-white/5">
                Annuleren
              </button>
              <button 
                onClick={() => resetPin(showResetPinModal)} 
                disabled={actionLoading || newPin.length !== 4} 
                className="flex-1 py-2 bg-purple-500 text-white font-medium rounded-lg hover:bg-purple-600 disabled:opacity-50"
              >
                {actionLoading ? 'Opslaan...' : 'PIN Opslaan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reregister Modal */}
      {showReregisterModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="glass rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <RotateCcw className="text-orange-400" size={20} />
                Account Reset
              </h2>
              <button onClick={() => setShowReregisterModal(null)} className="p-2 hover:bg-white/5 rounded-lg text-[#A8A29E]">
                <X size={20} />
              </button>
            </div>
            
            {!qrCode ? (
              <>
                <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg mb-4">
                  <p className="text-orange-400 text-sm">
                    ⚠️ Dit reset het volledige account. De beheerder moet opnieuw registreren met een nieuw wachtwoord en 2FA.
                  </p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setShowReregisterModal(null)} className="flex-1 py-2 border border-[#2C1B18] text-[#A8A29E] rounded-lg hover:bg-white/5">
                    Annuleren
                  </button>
                  <button onClick={() => reregisterManager(showReregisterModal)} disabled={actionLoading} className="flex-1 py-2 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 disabled:opacity-50">
                    {actionLoading ? 'Resetten...' : 'Account Reset'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-[#A8A29E] mb-4 text-center">Nieuwe registratiegegevens:</p>
                
                {newPassword && (
                  <div className="p-3 bg-[#1C1917] rounded-lg mb-4">
                    <p className="text-[#57534E] text-xs mb-1">Tijdelijk wachtwoord:</p>
                    <p className="text-[#D4AF37] font-mono select-all">{newPassword}</p>
                  </div>
                )}
                
                <p className="text-[#57534E] text-sm mb-2 text-center">Nieuwe QR code voor 2FA:</p>
                <div className="flex justify-center mb-4">
                  <div className="p-4 bg-white rounded-xl">
                    <img src={qrCode} alt="QR Code" className="w-48 h-48" />
                  </div>
                </div>
                <button onClick={() => { setShowReregisterModal(null); loadManagers(); }} className="w-full py-2 bg-[#D4AF37] text-black font-medium rounded-lg">
                  Sluiten
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManagers;
